import { useGetProductUnitCountsQuery } from 'gql/types';
import { useMemo } from 'react';

import { getDateRangeBounds } from '../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { DateRangeType } from '../types/phlebotomist-dashboard';

export interface IRecruiterProductStats {
  /** Total successful donations */
  totalSuccessfulDonations: number;
  /** Total pRBC units produced */
  totalPRBC: number;
  /** Total FFP units from single donors */
  totalFFPSingle: number;
  /** Total FFP units from double donors */
  totalFFPDouble: number;
  /** Total FFP units (single + double) */
  totalFFP: number;
  /** Total product units (pRBC + FFP) */
  totalUnits: number;
  /** Expected pRBC units */
  expectedPRBC: number;
  /** Expected FFP single units */
  expectedFFPSingle: number;
  /** Expected FFP double units */
  expectedFFPDouble: number;
  /** Expected total units */
  expectedTotal: number;
  /** pRBC success rate (actual/expected * 100) */
  prbcSuccessRate: number;
  /** FFP success rate (actual/expected * 100) */
  ffpSuccessRate: number;
  /** Overall success rate */
  overallSuccessRate: number;
}

export interface IUseRecruiterProductStatsProps {
  dateRange: DateRangeType;
  customDateRange?: { start: Date; end: Date };
  regionIds?: string[];
  officeIds?: string[];
  locationIds?: string[];
  ffpSglProductIds?: number[];
  ffpDblProductIds?: number[];
}

export interface IUseRecruiterProductStatsResult {
  stats: IRecruiterProductStats;
  loading: boolean;
  error?: Error;
}

// WooCommerce category IDs
const PRBC_CATEGORY = 31;
const FFP_CATEGORY = 33;

export function useRecruiterProductStats(
  props: IUseRecruiterProductStatsProps
): IUseRecruiterProductStatsResult {
  const {
    dateRange,
    customDateRange,
    regionIds,
    officeIds,
    locationIds,
    ffpSglProductIds,
    ffpDblProductIds,
  } = props;

  const dateBounds = useMemo(
    () => getDateRangeBounds(dateRange, customDateRange),
    [
      dateRange,
      customDateRange?.start?.getTime(),
      customDateRange?.end?.getTime(),
    ]
  );

  const sharedInput = useMemo(
    () => ({
      periodRange: { start: dateBounds.start, end: dateBounds.end },
      regionIds: regionIds?.length ? regionIds : undefined,
      locationIds: locationIds?.length ? locationIds : undefined,
      officeIds: officeIds?.length ? officeIds : undefined,
    }),
    [dateBounds, regionIds, locationIds, officeIds]
  );

  // Fetch pRBC counts
  const {
    data: prbcData,
    loading: prbcLoading,
    error: prbcError,
  } = useGetProductUnitCountsQuery({
    variables: {
      input: { ...sharedInput, woocommerceCategoryId: PRBC_CATEGORY },
    },
  });

  // Fetch FFP counts
  const {
    data: ffpData,
    loading: ffpLoading,
    error: ffpError,
  } = useGetProductUnitCountsQuery({
    variables: {
      input: {
        ...sharedInput,
        woocommerceCategoryId: FFP_CATEGORY,
        ffpSglProductIds: ffpSglProductIds?.length
          ? ffpSglProductIds
          : undefined,
        ffpDblProductIds: ffpDblProductIds?.length
          ? ffpDblProductIds
          : undefined,
      },
    },
  });

  const loading = prbcLoading || ffpLoading;
  const error = prbcError || ffpError;

  const stats: IRecruiterProductStats = useMemo(() => {
    const totalPRBC = prbcData?.getProductUnitCounts?.unitCount ?? 0;
    const totalFFP = ffpData?.getProductUnitCounts?.unitCount ?? 0;
    const totalUnits = totalPRBC + totalFFP;

    return {
      totalSuccessfulDonations: 0,
      totalPRBC,
      totalFFPSingle: 0,
      totalFFPDouble: 0,
      totalFFP,
      totalUnits,
      expectedPRBC: 0,
      expectedFFPSingle: 0,
      expectedFFPDouble: 0,
      expectedTotal: 0,
      prbcSuccessRate: 0,
      ffpSuccessRate: 0,
      overallSuccessRate: 0,
    };
  }, [prbcData, ffpData]);

  return {
    stats,
    loading,
    error: error as Error | undefined,
  };
}
